import React, { useEffect, useRef } from 'react'
import { reduxForm, Field } from 'redux-form'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import {
  SingleSelect,
  SingleSelectField,
  SingleOption,
  Button,
  ButtonLink,
  ButtonLoader,
  FormErrorNotification,
  requiredField,
  FormLabel,
} from '@offerzen/pinata'

import styles from './styles'

const validators = {
  required: requiredField('Required'),
}

const RoleForm = ({ form, ...props }) => {
  const roleFieldRef = useRef(null)

  useEffect(() => {
    roleFieldRef.current.focus()
  }, [])

  return (
    <div className={styles.wrapper} data-test="preferred-role-form">
      <If condition={props.error}>
        <FormErrorNotification text={props.error} />
      </If>

      <div className={styles.fieldRow}>
        <div className={cx(styles.fieldGroup, styles.roleFieldGroup)}>
          <FormLabel label="Desired role" htmlFor={`${form}-name`} />
          <Field
            component={SingleSelectField}
            id={`${form}-name`}
            placeholder="- select a role -"
            name="job_role_id"
            dataTest="job_role_id"
            validate={validators.required}
            className={cx(styles.field, styles.rolesOptions)}
            readOnly={!props.isNew}
            setInnerRef={el => {
              roleFieldRef.current = el
            }}>
            <Choose>
              <When condition={props.isNew}>
                <For each="role" of={props.rolesList}>
                  <SingleOption label={role.name} value={role.id} key={role.id} />
                </For>
              </When>
              <Otherwise>
                <SingleOption label={props.name} value={props.initialValues.job_role_id} />
              </Otherwise>
            </Choose>
          </Field>
        </div>

        <div className={cx(styles.fieldGroup, styles.experienceFieldGroup)}>
          <Field
            component={SingleSelect}
            id={`${form}-years-experience`}
            placeholder="- select years of experience -"
            name="years_experience"
            dataTest="years_experience"
            validate={validators.required}
            label="Current experience in role"
            selectClassName={styles.field}>
            <For each="years" of={props.yearsExperienceList}>
              <SingleOption label={years} value={years} key={years} />
            </For>
          </Field>
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <ButtonLink
          theme="light"
          onClick={props.isNew ? props.deleteBlankJobExperienceItem : props.toggleJobExperienceEdit}
          label="Cancel"
          className={styles.cancelButton}
          disabled={props.submitting}
        />

        <Choose>
          <When condition={props.submitting}>
            <ButtonLoader variant="outlined" size="medium" label="Saving" />
          </When>
          <Otherwise>
            <Button variant="outlined" onClick={props.handleSubmit} label="Save" size="medium" />
          </Otherwise>
        </Choose>
      </div>
    </div>
  )
}

RoleForm.propTypes = {
  form: PropTypes.string,
  isNew: PropTypes.bool,
  rolesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  yearsExperienceList: PropTypes.arrayOf(PropTypes.string),
  createJobExperience: PropTypes.func,
  updateJobExperience: PropTypes.func,
  toggleJobExperienceEdit: PropTypes.func,
  deleteBlankJobExperienceItem: PropTypes.func,
  name: PropTypes.string,
  error: PropTypes.string,
  initialValues: PropTypes.shape({
    id: PropTypes.string,
    job_role_id: PropTypes.string,
    years_experience: PropTypes.string,
  }),
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
}

export default reduxForm({
  onSubmit: (values, _, props) =>
    new Promise((resolve, reject) => {
      if (props.isNew) {
        props.createJobExperience(values, resolve, reject)
        return
      }

      props.updateJobExperience(values, resolve, reject)
    }),
  onSubmitSuccess: (_, __, props) => {
    if (props.isNew) {
      props.deleteBlankJobExperienceItem()
      return
    }

    props.toggleJobExperienceEdit()
  },
})(RoleForm)
